import { useState, type ReactNode } from "react";

import { useClips } from "../hooks/useClips";
import { horaComSegundos, num } from "../lib/format";

/** Quantos instantes cabem na fila de botões sem virar papel de parede. */
const MAX_BOTOES = 12;

interface Props {
  code: string;
  de: Date;
  ate: Date;
}

/**
 * Os clips de vídeo da boia do painel, no intervalo escolhido.
 *
 * Um leitor e uma fila de instantes, mais recentes primeiro — a pergunta a
 * que isto responde é "o que é que a câmara viu às 14:32", com os gráficos
 * ao lado a dizer porque é que se quer saber. O clip mais recente entra
 * selecionado: é o "está viva e vê isto" de quem abre o painel.
 *
 * `preload="metadata"` e não `auto`: o painel abre-se muitas vezes só para
 * ver os gráficos, e descarregar 15 MB por abertura era pagar 4G... do lado
 * de quem vê, mas o princípio mantém-se — só se transfere o que se pede.
 */
export function Clips({ code, de, ate }: Props): ReactNode {
  const { clips, erro, aRecarregar, truncado } = useClips(code, de, ate);
  const [escolhido, setEscolhido] = useState<string | null>(null);

  // A API devolve por ordem cronológica; aqui interessa o mais recente primeiro.
  const recentes = [...clips].reverse();
  // Se o escolhido saiu do intervalo (ou nunca houve escolha), cai no mais novo.
  const ativo = recentes.find((c) => c.clip_uuid === escolhido) ?? recentes[0] ?? null;

  return (
    <div className="seccao">
      <h3>Vídeo</h3>
      {erro !== null && (
        <p className="aviso-erro" style={{ marginTop: 0 }}>
          {erro}
        </p>
      )}
      <div className={aRecarregar ? "a-recarregar" : undefined}>
        {ativo === null ? (
          erro === null && (
            <p className="nota" style={{ margin: 0 }}>
              Sem clips neste intervalo. O nó envia 1 clip de 60 s em cada 2 min, e
              imediatamente sempre que deteta movimento debaixo da boia.
            </p>
          )
        ) : (
          <div className="video-caixa">
            {/* key força o browser a trocar de fonte; sem ela o <video> fica
                agarrado ao clip anterior ao mudar a seleção. */}
            <video key={ativo.clip_uuid} controls preload="metadata" src={ativo.media_url} />
            <p className="nota" style={{ marginTop: 6, marginBottom: 0 }}>
              {horaComSegundos(ativo.clip_start)}–{horaComSegundos(ativo.clip_end)} ·{" "}
              {ativo.camera} · {num(ativo.size_bytes / 1e6, 1)} MB
            </p>
          </div>
        )}
        {recentes.length > 1 && (
          <div className="filtros" role="group" aria-label="Escolher clip" style={{ marginTop: 8 }}>
            {recentes.slice(0, MAX_BOTOES).map((clip) => (
              <button
                key={clip.clip_uuid}
                type="button"
                aria-pressed={clip.clip_uuid === ativo?.clip_uuid}
                onClick={() => setEscolhido(clip.clip_uuid)}
              >
                {horaComSegundos(clip.clip_start)}
              </button>
            ))}
          </div>
        )}
        {(recentes.length > MAX_BOTOES || truncado) && (
          <p className="nota" style={{ marginTop: 6, marginBottom: 0 }}>
            Mostrados os {Math.min(recentes.length, MAX_BOTOES)} mais recentes de{" "}
            {recentes.length}
            {truncado ? "+" : ""} no intervalo. Encurta a janela para ver os restantes.
          </p>
        )}
      </div>
    </div>
  );
}
